import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  Search,
  Sparkles,
  ChevronRight,
  RotateCcw,
  CheckCircle2,
  Circle,
  TrendingUp,
  Clock,
  ArrowRight,
  Zap,
  Globe,
  BookOpen,
  X,
  Link2,
  Tag,
  PenLine,
  Compass,
} from 'lucide-react';
import {
  PRESET_DOMAINS,
  PRESET_DOMAIN_IDS,
  generateTrendsForDomain,
  generateTrendsForDomainAsync,
  saveSession,
  getLastSession,
  getDomainHistory,
  type Domain,
  type DiscoveredTrend,
  type DomainSession,
} from '../services/domainEngine';

// Local alias used in handleDiscover
const TREND_SEEDS_IDS = PRESET_DOMAIN_IDS;

type Step = 'domain-select' | 'discovering' | 'trend-select';
type InputMode = 'topic' | 'url';

const STATUS_COLORS: Record<DiscoveredTrend['status'], string> = {
  trending: 'text-violet-400 bg-violet-400/10 border-violet-400/30',
  emerging: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30',
  stable: 'text-green-400 bg-green-400/10 border-green-400/30',
  declining: 'text-red-400 bg-red-400/10 border-red-400/30',
};

const CONTENT_TYPES = [
  { id: 'blog', label: 'Blog Post', icon: '📄', description: '1,200-word SEO article' },
  { id: 'infographic', label: 'Infographic Script', icon: '📊', description: 'Data-point brief + layout' },
  { id: 'reel', label: 'Reel / Short Script', icon: '🎬', description: '30-second talking avatar script' },
  { id: 'caption', label: 'Social Caption', icon: '💬', description: 'Platform-native captions' },
  { id: 'email', label: 'Email Newsletter', icon: '✉️', description: 'Curated trend digest' },
];

const DISCOVER_STEPS = [
  'Scanning knowledge sources…',
  'Analysing publication velocity…',
  'Scoring relevance & sentiment…',
  'Ranking by growth rate…',
  'Preparing trend cards…',
];

const DISCOVER_STEPS_URL = [
  'Fetching website content…',
  'Identifying domain, products & services…',
  'Analysing industry landscape…',
  'Generating contextual trends…',
  'Ranking by relevance…',
];

// Helper: extract a clean label and keywords from a URL (NO industry guessing — scrape handles that)
function parseDomainFromUrl(rawUrl: string): { label: string; description: string; keywords: string[] } | null {
  try {
    let url = rawUrl.trim();
    if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
    const parsed = new URL(url);
    const hostname = parsed.hostname.replace(/^www\./, '');
    const domainBase = hostname.split('.')[0];

    // Use domain name as label — scrape will fill in the real context
    const label = `${domainBase.charAt(0).toUpperCase()}${domainBase.slice(1)}`;

    // Minimal description — scrape will override this with real content
    const description = `Website: ${url}. Industry and services to be determined by scraping the website content.`;

    // Keywords = just the domain name parts; AI will derive real ones from scraped content
    const keywords = [domainBase, hostname].filter(Boolean);

    return { label, description, keywords };
  } catch {
    return null;
  }
}

// Industry hint patterns — used ONLY for manually typed custom text (not URLs)
const CUSTOM_TEXT_INDUSTRY_HINTS: Array<[RegExp, string]> = [
  [/hotel|resort|hospitality|motel|lodge|inn|accommodation/i, 'hotel and hospitality industry'],
  [/restaurant|cafe|food|beverage|kitchen|catering|dining/i, 'food & beverage industry'],
  [/clinical|clinical trial|pharma|healthcare|hospital|medical|patient/i, 'healthcare and clinical research'],
  [/retail|shop|ecommerce|fashion|apparel|store/i, 'retail and e-commerce'],
  [/bank|finance|fintech|insurance|investment|wealth/i, 'financial services'],
  [/school|university|education|learning|edtech|training/i, 'education and EdTech'],
  [/legal|law|attorney|compliance|solicitor/i, 'legal services'],
  [/real estate|property|realty|construction|architecture/i, 'real estate and property'],
  [/automotive|car|vehicle|mobility|transport/i, 'automotive and mobility'],
  [/travel|tourism|airline|cruise|destination/i, 'travel and tourism'],
];

function inferIndustryFromText(text: string): string {
  for (const [pattern, industry] of CUSTOM_TEXT_INDUSTRY_HINTS) {
    if (pattern.test(text)) return industry;
  }
  return '';
}

const TrendDiscovery: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('domain-select');
  const [inputMode, setInputMode] = useState<InputMode>('topic');
  const [domainSearch, setDomainSearch] = useState('');
  const [customDomain, setCustomDomain] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [trends, setTrends] = useState<DiscoveredTrend[]>([]);
  const [selectedTrendIds, setSelectedTrendIds] = useState<Set<string>>(new Set());
  const [selectedContentTypes, setSelectedContentTypes] = useState<Set<string>>(new Set(['blog', 'infographic', 'reel']));
  const [discoverProgress, setDiscoverProgress] = useState(0);
  const [discoverStepLabel, setDiscoverStepLabel] = useState('');
  const [filterQuery, setFilterQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [domainHistory, setDomainHistory] = useState<Domain[]>([]);
  const [resumedSession, setResumedSession] = useState(false);

  useEffect(() => {
    setDomainHistory(getDomainHistory());
    const last = getLastSession();
    if (last) {
      setResumedSession(false);
    }
  }, []);

  const filteredPresetDomains = PRESET_DOMAINS.filter((d) =>
    !domainSearch ||
    d.label.toLowerCase().includes(domainSearch.toLowerCase()) ||
    d.description.toLowerCase().includes(domainSearch.toLowerCase())
  );

  // ── Domain Selection ────────────────────────────────────────────────────

  const handleSelectDomain = (domain: Domain) => {
    setSelectedDomain(domain);
    setCustomDomain('');
    // Clear any stale results from a previous run
    setTrends([]);
    setSelectedTrendIds(new Set());
  };

  const handleCustomDomainConfirm = () => {
    if (!customDomain.trim()) return;
    // Clear stale results from previous custom topic search
    setTrends([]);
    setSelectedTrendIds(new Set());
    const label = customDomain.trim();
    // Keywords: split by comma/semicolon but keep multi-word phrases intact
    const words = label.split(/[,;|]+/).map((w) => w.trim()).filter((w) => w.length > 1);
    // Infer industry from typed text using URL hints (works for text too)
    const industryHint = inferIndustryFromText(label + ' ' + words.join(' '));
    const custom: Domain = {
      id: `custom-${Date.now()}`,
      label,
      // Rich description so Gemini/fuzzy match understands the domain
      description: industryHint
        ? `Industry: ${industryHint}. Topic: ${label}. Discover current 2025-2026 trends, innovations, consumer behaviours, and market shifts specifically within this sector.`
        : `Topic: ${label}. Discover current 2025-2026 trends, innovations, and market shifts specifically within the ${label} sector.`,
      icon: '🏷️',
      color: '#a78bfa',
      bgGradient: 'from-violet-500/20 to-purple-500/10',
      sampleKeywords: words.length > 0 ? words : [label],
    };
    setSelectedDomain(custom);
  };

  const handleUrlConfirm = () => {
    setUrlError('');
    // Clear stale results from previous URL search
    setTrends([]);
    setSelectedTrendIds(new Set());
    const parsed = parseDomainFromUrl(websiteUrl);
    if (!parsed) {
      setUrlError('Could not parse a valid URL. Try: https://example.com/products or just example.com');
      return;
    }
    // Normalise the URL
    let cleanUrl = websiteUrl.trim();
    if (!/^https?:\/\//i.test(cleanUrl)) cleanUrl = 'https://' + cleanUrl;

    const domain: Domain = {
      id: `url-${Date.now()}`,
      label: parsed.label,
      description: parsed.description, // minimal — real context comes from scrape
      icon: '🌐',
      color: '#34d399',
      bgGradient: 'from-emerald-500/20 to-teal-500/10',
      sampleKeywords: parsed.keywords,
      url: cleanUrl, // passed to domainEngine for live scraping
    };
    setSelectedDomain(domain);
  };

  const handleResumeSession = () => {
    const last = getLastSession();
    if (!last) return;
    setSelectedDomain(last.domain);
    setTrends(last.trends);
    setSelectedTrendIds(new Set(last.selectedTrendIds));
    setStep('trend-select');
    setResumedSession(true);
  };

  // ── Trend Discovery Simulation ──────────────────────────────────────────

  const handleDiscover = async () => {
    if (!selectedDomain) return;
    // ━━ Hard-reset all previous results before new search ━━
    setTrends([]);
    setSelectedTrendIds(new Set());
    setFilterQuery('');
    setFilterStatus('all');
    setDiscoverProgress(0);
    setDiscoverStepLabel('');
    setStep('discovering');

    const isPreset = !!TREND_SEEDS_IDS.has(selectedDomain.id);

    // For URL domains, first scrape step is shown; for custom, AI step
    const isUrl = !!selectedDomain.url;

    // Start AI fetch immediately in parallel (for custom/URL domains)
    const trendPromise = generateTrendsForDomainAsync(selectedDomain);

    // Animate progress concurrently — steps pace differently by type
    const stepDelay = isPreset ? 350 : isUrl ? 2200 : 1800;
    const steps = isUrl ? DISCOVER_STEPS_URL : DISCOVER_STEPS;
    for (let i = 0; i < steps.length; i++) {
      setDiscoverStepLabel(steps[i]);
      // For AI/URL domains, keep progress at 90% max until results are ready
      const maxProgress = isPreset ? 100 : 90;
      const stepProgress = Math.round(((i + 1) / steps.length) * maxProgress);
      await new Promise((r) => setTimeout(r, stepDelay + Math.random() * 400));
      setDiscoverProgress(stepProgress);
    }

    // Wait for AI results (already running in background)
    const discovered = await trendPromise;

    setDiscoverProgress(100);
    setDiscoverStepLabel('Trends ready!');
    await new Promise((r) => setTimeout(r, 400));

    setTrends(discovered);
    setSelectedTrendIds(new Set());
    setStep('trend-select');

    const session: DomainSession = {
      domain: selectedDomain,
      trends: discovered,
      selectedTrendIds: [],
      discoveredAt: new Date().toISOString(),
    };
    saveSession(session);
    setDomainHistory(getDomainHistory());
  };

  // ── Trend Selection ─────────────────────────────────────────────────────

  const toggleTrend = (id: string) => {
    setSelectedTrendIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllTrends = () => {
    if (selectedTrendIds.size === filteredTrends.length) {
      setSelectedTrendIds(new Set());
    } else {
      setSelectedTrendIds(new Set(filteredTrends.map((t) => t.id)));
    }
  };

  const toggleContentType = (id: string) => {
    setSelectedContentTypes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSaveSelections = () => {
    if (!selectedDomain) return;
    const session: DomainSession = {
      domain: selectedDomain,
      trends,
      selectedTrendIds: Array.from(selectedTrendIds),
      discoveredAt: new Date().toISOString(),
    };
    saveSession(session);
    navigate('/pipeline');
  };

  const handleReset = () => {
    setStep('domain-select');
    setSelectedDomain(null);
    setTrends([]);
    setSelectedTrendIds(new Set());
    setCustomDomain('');
    setFilterQuery('');
    setFilterStatus('all');
    setResumedSession(false);
  };

  const filteredTrends = trends.filter((t) => {
    const q = filterQuery.toLowerCase();
    const matchesQuery = !q || t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q) || t.keywords.some((k) => k.toLowerCase().includes(q));
    const matchesStatus = filterStatus === 'all' || t.status === filterStatus;
    return matchesQuery && matchesStatus;
  });

  const lastSession = getLastSession();

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="p-3 sm:p-6 max-w-6xl mx-auto space-y-4 sm:space-y-6">

        {/* ── Page Header ── */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2.5">
              <Compass className="w-6 h-6 text-violet-400" />
              Discover Emerging Content Opportunities
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              Choose your domain · Surface what's trending · Select topics · Generate publish-ready assets
            </p>
          </div>
          {step !== 'domain-select' && (
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:bg-white/10 transition-all text-sm"
            >
              <RotateCcw className="w-4 h-4" />
              New Discovery
            </button>
          )}
        </div>

        {/* ── KPI Cards ── */}
        {step === 'domain-select' && (
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Trends Identified', value: '342', icon: TrendingUp, color: 'text-violet-400', bg: 'bg-violet-500/10 border-violet-500/20' },
              { label: 'Topics Generated',  value: '127', icon: Zap,        color: 'text-cyan-400',   bg: 'bg-cyan-500/10 border-cyan-500/20'   },
              { label: 'Est. Reach',         value: '475K',icon: Globe,      color: 'text-emerald-400',bg: 'bg-emerald-500/10 border-emerald-500/20' },
            ].map(k => (
              <div key={k.label} className={`rounded-2xl border p-3 ${k.bg}`}>
                <div className="flex items-center gap-2 mb-2">
                  <k.icon className={`w-3.5 h-3.5 ${k.color}`} />
                  <span className="text-[10px] text-slate-500">{k.label}</span>
                </div>
                <p className={`text-xl font-bold ${k.color}`}>{k.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* ── Step Indicator ── */}
        <div className="flex items-center gap-2 text-sm">
          {(['domain-select', 'discovering', 'trend-select'] as Step[]).map((s, i) => {
            const labels = ['1. Choose Domain', '2. Discovering', '3. Select Topics'];
            const isActive = step === s;
            const isDone = (['domain-select', 'discovering', 'trend-select'] as Step[]).indexOf(step) > i;
            return (
              <React.Fragment key={s}>
                <span className={`px-3 py-1 rounded-full font-medium transition-all ${isActive ? 'bg-violet-500/20 text-violet-300 border border-violet-500/40' : isDone ? 'text-green-400' : 'text-slate-500'}`}>
                  {isDone ? '✓ ' : ''}{labels[i]}
                </span>
                {i < 2 && <ChevronRight className="w-4 h-4 text-slate-600" />}
              </React.Fragment>
            );
          })}
        </div>

        {/* ═══════════════════════════════════════════════════════════════
            STEP 1 — Topic / Domain Selection
        ═══════════════════════════════════════════════════════════════ */}
        {step === 'domain-select' && (
          <div className="space-y-5">

            {/* Resume last session banner */}
            {lastSession && !resumedSession && (
              <div className="flex items-center justify-between p-4 rounded-xl bg-violet-500/10 border border-violet-500/20">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-violet-400" />
                  <div>
                    <p className="text-sm font-medium text-white">
                      Resume: <span className="text-violet-300">{lastSession.domain.label}</span>
                    </p>
                    <p className="text-xs text-slate-400">
                      {lastSession.trends.length} trends · {lastSession.selectedTrendIds.length} selected · {new Date(lastSession.discoveredAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleResumeSession}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-sm font-medium transition-all"
                >
                  Resume <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* ── Two clear mode buttons ─────────────────────────────────── */}
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => { setInputMode('topic'); setSelectedDomain(null); setUrlError(''); }}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${
                  inputMode === 'topic'
                    ? 'border-violet-500 bg-violet-500/10 text-white'
                    : 'border-white/10 bg-white/3 text-slate-400 hover:border-white/25 hover:text-slate-200'
                }`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  inputMode === 'topic' ? 'bg-violet-500/30' : 'bg-white/8'
                }`}>
                  <Tag className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold text-sm leading-tight">Open Topic</p>
                  <p className="text-xs text-slate-500 mt-0.5">Browse domains or type any topic</p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => { setInputMode('url'); setSelectedDomain(null); }}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${
                  inputMode === 'url'
                    ? 'border-emerald-500 bg-emerald-500/10 text-white'
                    : 'border-white/10 bg-white/3 text-slate-400 hover:border-white/25 hover:text-slate-200'
                }`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  inputMode === 'url' ? 'bg-emerald-500/30' : 'bg-white/8'
                }`}>
                  <Globe className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold text-sm leading-tight">Website URL</p>
                  <p className="text-xs text-slate-500 mt-0.5">Extract topic from a URL</p>
                </div>
              </button>
            </div>

            {/* ════════════════════════════════════════════════════════════
                OPTION A — Open Topic
            ════════════════════════════════════════════════════════════ */}
            {inputMode === 'topic' && (
              <div className="space-y-4">

                {/* Free-text input */}
                <div className="bg-slate-900/70 border border-white/10 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <PenLine className="w-4 h-4 text-violet-400" />
                    <span className="text-sm font-semibold text-white">Type Your Own Topic</span>
                  </div>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={customDomain}
                      onChange={(e) => {
                        setCustomDomain(e.target.value);
                        if (selectedDomain?.id.startsWith('custom-')) setSelectedDomain(null);
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && handleCustomDomainConfirm()}
                      placeholder="e.g. Healthcare AI, EdTech, B2B SaaS, Fintech UX…"
                      className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-violet-500 transition-all text-sm"
                    />
                    <button
                      type="button"
                      onClick={handleCustomDomainConfirm}
                      disabled={!customDomain.trim()}
                      className="px-5 py-3 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold transition-all disabled:opacity-40 flex-shrink-0"
                    >
                      Use This
                    </button>
                  </div>
                  {selectedDomain?.id.startsWith('custom-') && (
                    <div className="flex items-center gap-2 mt-3 text-sm text-violet-300">
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                      <span>Topic set: <strong>{selectedDomain.label}</strong></span>
                    </div>
                  )}
                </div>

                {/* Preset domain list — always visible in the page, no dropdown */}
                <div className="bg-slate-900/70 border border-white/10 rounded-2xl overflow-hidden">
                  <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-white/8">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <span className="text-sm font-semibold text-white">Or Choose a Preset Domain</span>
                      <span className="px-2 py-0.5 rounded-full bg-slate-700 text-[10px] text-slate-400">{PRESET_DOMAINS.length}</span>
                    </div>
                    {selectedDomain && PRESET_DOMAINS.find((d) => d.id === selectedDomain.id) && (
                      <button
                        type="button"
                        onClick={() => setSelectedDomain(null)}
                        className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1 transition-colors"
                      >
                        <X className="w-3 h-3" /> Clear
                      </button>
                    )}
                  </div>

                  <div className="px-4 py-2.5 border-b border-white/8">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                      <input
                        type="text"
                        value={domainSearch}
                        onChange={(e) => setDomainSearch(e.target.value)}
                        placeholder="Search preset domains…"
                        className="w-full pl-9 pr-3 py-2 bg-white/5 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-violet-500/50 transition-all"
                      />
                    </div>
                  </div>

                  <div className="max-h-80 overflow-y-auto divide-y divide-white/5">
                    {filteredPresetDomains.length === 0 && (
                      <p className="text-center text-slate-500 text-sm py-8">No domains match</p>
                    )}
                    {filteredPresetDomains.map((domain) => {
                      const isSelected = selectedDomain?.id === domain.id;
                      return (
                        <button
                          key={domain.id}
                          type="button"
                          onClick={() => handleSelectDomain(domain)}
                          className={`w-full flex items-center gap-4 px-5 py-3.5 text-left transition-colors ${
                            isSelected
                              ? 'bg-violet-500/15 border-l-2 border-l-violet-500'
                              : 'hover:bg-white/5 border-l-2 border-l-transparent'
                          }`}
                        >
                          <span className="text-2xl flex-shrink-0">{domain.icon}</span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${isSelected ? 'text-violet-300' : 'text-white'}`}>
                              {domain.label}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5 truncate">{domain.description}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <div className="hidden sm:flex gap-1">
                              {domain.sampleKeywords.slice(0, 2).map((kw) => (
                                <span key={kw} className="px-1.5 py-0.5 rounded bg-white/8 text-[10px] text-slate-400">{kw}</span>
                              ))}
                            </div>
                            {isSelected
                              ? <CheckCircle2 className="w-5 h-5 text-violet-400" />
                              : <ChevronRight className="w-4 h-4 text-slate-600" />
                            }
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* ════════════════════════════════════════════════════════════
                OPTION B — Website URL
            ════════════════════════════════════════════════════════════ */}
            {inputMode === 'url' && (
              <div className="bg-slate-900/70 border border-white/10 rounded-2xl p-5 space-y-4">
                <p className="text-slate-400 text-sm leading-relaxed">
                  Paste your website or product page URL. We'll extract the topic/domain and discover the most relevant industry trends.
                </p>

                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="text"
                      value={websiteUrl}
                      onChange={(e) => {
                        setWebsiteUrl(e.target.value);
                        setUrlError('');
                        if (selectedDomain?.id.startsWith('url-')) setSelectedDomain(null);
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && handleUrlConfirm()}
                      placeholder="yoursite.com  or  https://yoursite.com/products"
                      className={`w-full pl-10 pr-4 py-3 bg-white/5 border rounded-xl text-white placeholder-slate-500 focus:outline-none transition-all text-sm ${
                        urlError
                          ? 'border-red-500/50 focus:border-red-400'
                          : 'border-white/10 focus:border-emerald-500'
                      }`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleUrlConfirm}
                    disabled={!websiteUrl.trim()}
                    className="px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold transition-all disabled:opacity-40 flex-shrink-0"
                  >
                    Extract Topic
                  </button>
                </div>

                {urlError && (
                  <p className="flex items-center gap-2 text-red-400 text-xs">
                    <X className="w-3.5 h-3.5 flex-shrink-0" /> {urlError}
                  </p>
                )}

                {selectedDomain?.id.startsWith('url-') && (
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/25">
                    <span className="text-2xl flex-shrink-0">{selectedDomain.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white">{selectedDomain.label}</p>
                      <p className="text-xs text-slate-400 mt-0.5 truncate">{selectedDomain.description}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {selectedDomain.sampleKeywords.map((kw) => (
                          <span key={kw} className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-[11px] text-emerald-300">{kw}</span>
                        ))}
                      </div>
                    </div>
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  </div>
                )}

                <div className="border-t border-white/8 pt-3">
                  <p className="text-xs text-slate-500 mb-2">Quick examples — click to fill:</p>
                  <div className="flex flex-wrap gap-2">
                    {['stripe.com/payments', 'notion.so/product', 'hubspot.com/marketing', 'shopify.com/commerce'].map((ex) => (
                      <button
                        key={ex}
                        type="button"
                        onClick={() => {
                          setWebsiteUrl(ex);
                          setUrlError('');
                          if (selectedDomain?.id.startsWith('url-')) setSelectedDomain(null);
                        }}
                        className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 hover:text-white hover:border-emerald-500/40 hover:bg-emerald-500/5 transition-all"
                      >
                        {ex}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Domain history */}
            {domainHistory.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> Recently used
                </p>
                <div className="flex flex-wrap gap-2">
                  {domainHistory.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setSelectedDomain(d)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        selectedDomain?.id === d.id
                          ? 'bg-violet-500/20 border-violet-400/40 text-violet-300'
                          : 'bg-white/5 border-white/10 text-slate-300 hover:border-white/25'
                      }`}
                    >
                      {d.icon} {d.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Discover button */}
            <div className="flex items-center gap-4 pt-1">
              <button
                type="button"
                onClick={handleDiscover}
                disabled={!selectedDomain}
                className="flex items-center gap-3 px-8 py-4 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold text-base transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_0_30px_rgba(139,92,246,0.4)] hover:shadow-[0_0_40px_rgba(139,92,246,0.6)]"
              >
                <Sparkles className="w-5 h-5" />
                Discover Trends
                {selectedDomain && (
                  <span className="text-violet-200 font-normal text-sm">for {selectedDomain.label}</span>
                )}
              </button>
              {!selectedDomain && (
                <p className="text-slate-500 text-sm">← Pick a topic or enter a URL first</p>
              )}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            STEP 2 — Discovery Animation
        ═══════════════════════════════════════════════════════════════ */}
        {step === 'discovering' && (
          <div className="flex flex-col items-center justify-center py-24 space-y-8">
            <div className="relative w-24 h-24">
              <div className="absolute inset-0 rounded-full border-4 border-violet-500/20 animate-ping" />
              <div className="absolute inset-2 rounded-full border-4 border-violet-500/40 animate-spin" style={{ animationDuration: '2s' }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-violet-400" />
              </div>
            </div>

            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-white">
                Discovering trends in <span className="text-violet-400">{selectedDomain?.label}</span>
              </h2>
              <p className="text-slate-400 text-sm">{discoverStepLabel}</p>
            </div>

            <div className="w-full max-w-md">
              <div className="flex justify-between text-xs text-slate-500 mb-2">
                <span>Scanning 65+ sources</span>
                <span>{discoverProgress}%</span>
              </div>
              <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-violet-500 to-cyan-400 rounded-full transition-all duration-500"
                  style={{ width: `${discoverProgress}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            STEP 3 — Trend & Content Type Selection
        ═══════════════════════════════════════════════════════════════ */}
        {step === 'trend-select' && selectedDomain && (
          <div className="space-y-6">

            {/* Domain badge + stats */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{selectedDomain.icon}</span>
                <div>
                  <h2 className="text-white font-semibold">{selectedDomain.label}</h2>
                  <p className="text-slate-400 text-xs">
                    {trends.length} trends discovered · {selectedTrendIds.size} selected
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-medium">
                  ✓ Discovery complete
                </span>
                {resumedSession && (
                  <span className="px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-medium">
                    Resumed session
                  </span>
                )}
                {/* New Search — fully resets state */}
                <button
                  onClick={handleReset}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/30 text-violet-400 text-xs font-medium hover:bg-violet-500/20 transition-colors"
                >
                  <RotateCcw className="w-3 h-3" /> New Search
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

              {/* Left: Trend Cards */}
              <div className="lg:col-span-2 space-y-4">

                {/* Filter bar */}
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="text"
                      value={filterQuery}
                      onChange={(e) => setFilterQuery(e.target.value)}
                      placeholder="Filter trends…"
                      className="w-full pl-9 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-violet-500"
                    />
                    {filterQuery && (
                      <button onClick={() => setFilterQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-violet-500"
                  >
                    <option value="all">All Status</option>
                    <option value="trending">Trending</option>
                    <option value="emerging">Emerging</option>
                    <option value="stable">Stable</option>
                    <option value="declining">Declining</option>
                  </select>
                </div>

                {/* Select all / count */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={toggleAllTrends}
                    className="text-xs text-violet-400 hover:text-violet-300 font-medium transition-colors"
                  >
                    {selectedTrendIds.size === filteredTrends.length ? 'Deselect All' : 'Select All'}
                  </button>
                  <span className="text-xs text-slate-500">
                    {filteredTrends.length} of {trends.length} shown
                  </span>
                </div>

                {/* Trend list */}
                {filteredTrends.map((trend) => {
                  const isSelected = selectedTrendIds.has(trend.id);
                  return (
                    <button
                      key={trend.id}
                      onClick={() => toggleTrend(trend.id)}
                      className={`w-full text-left p-5 rounded-xl border transition-all duration-200 ${
                        isSelected
                          ? 'bg-violet-500/10 border-violet-400/40 shadow-[0_0_20px_rgba(139,92,246,0.2)]'
                          : 'bg-white/3 border-white/8 hover:bg-white/6 hover:border-white/20'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="mt-0.5 flex-shrink-0">
                          {isSelected ? (
                            <CheckCircle2 className="w-5 h-5 text-violet-400" />
                          ) : (
                            <Circle className="w-5 h-5 text-slate-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="font-semibold text-white text-sm">{trend.name}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs border font-medium ${STATUS_COLORS[trend.status]}`}>
                              {trend.status}
                            </span>
                            <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-xs text-slate-400">
                              {trend.category}
                            </span>
                          </div>
                          <p className="text-slate-400 text-xs leading-relaxed mb-3">{trend.description}</p>
                          <div className="flex items-center gap-4 text-xs">
                            <div className="flex items-center gap-1.5">
                              <TrendingUp className="w-3.5 h-3.5 text-cyan-400" />
                              <span className="text-cyan-400 font-medium">+{trend.growthRate}%</span>
                              <span className="text-slate-500">growth</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Zap className="w-3.5 h-3.5 text-amber-400" />
                              <span className="text-amber-400 font-medium">{trend.relevanceScore}</span>
                              <span className="text-slate-500">relevance</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <BookOpen className="w-3.5 h-3.5 text-slate-500" />
                              <span className="text-slate-400">{trend.sources[0]}</span>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-3">
                            {trend.keywords.slice(0, 4).map((kw) => (
                              <span key={kw} className="px-2 py-0.5 rounded-full bg-white/5 text-xs text-slate-400">
                                #{kw}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Growth bar */}
                        <div className="hidden sm:flex flex-col items-end gap-1 flex-shrink-0">
                          <div className="w-16 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-violet-500 to-cyan-400 rounded-full"
                              style={{ width: `${trend.growthRate}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-500">{trend.popularity}% popular</span>
                        </div>
                      </div>
                    </button>
                  );
                })}

                {filteredTrends.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <Search className="w-8 h-8 mx-auto mb-3 opacity-40" />
                    <p>No trends match your filter</p>
                  </div>
                )}
              </div>

              {/* Right: Content Types + CTA */}
              <div className="space-y-5">

                {/* Content type selector */}
                <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-violet-400" />
                    Content to Generate
                  </h3>
                  <div className="space-y-2">
                    {CONTENT_TYPES.map((ct) => {
                      const isOn = selectedContentTypes.has(ct.id);
                      return (
                        <button
                          key={ct.id}
                          onClick={() => toggleContentType(ct.id)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all ${
                            isOn
                              ? 'bg-violet-500/10 border-violet-400/30 text-white'
                              : 'bg-white/3 border-white/8 text-slate-400 hover:border-white/20'
                          }`}
                        >
                          <span className="text-base">{ct.icon}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium">{ct.label}</p>
                            <p className="text-xs text-slate-500 truncate">{ct.description}</p>
                          </div>
                          {isOn ? (
                            <CheckCircle2 className="w-4 h-4 text-violet-400 flex-shrink-0" />
                          ) : (
                            <Circle className="w-4 h-4 text-slate-600 flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* CTA */}
                <div className="p-5 rounded-xl bg-gradient-to-br from-violet-500/10 to-purple-500/5 border border-violet-500/20">
                  <p className="text-xs text-slate-400 mb-1">Ready to generate</p>
                  <p className="text-xl font-bold text-white mb-1">
                    {selectedTrendIds.size} topic{selectedTrendIds.size !== 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-slate-400 mb-4">
                    × {selectedContentTypes.size} content type{selectedContentTypes.size !== 1 ? 's' : ''} selected
                  </p>

                  <button
                    onClick={handleSaveSelections}
                    disabled={selectedTrendIds.size === 0 || selectedContentTypes.size === 0}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_0_20px_rgba(139,92,246,0.3)]"
                  >
                    <Sparkles className="w-4 h-4" />
                    Save & Go to Pipeline
                    <ArrowRight className="w-4 h-4" />
                  </button>

                  {selectedTrendIds.size === 0 && (
                    <p className="text-xs text-slate-500 text-center mt-2">← Select at least one trend</p>
                  )}
                </div>

                {/* Sources summary */}
                {selectedTrendIds.size > 0 && (
                  <div className="p-4 rounded-xl bg-white/3 border border-white/8">
                    <h4 className="text-xs font-medium text-slate-400 mb-2">Top Sources</h4>
                    <div className="space-y-1">
                      {Array.from(new Set(
                        trends
                          .filter((t) => selectedTrendIds.has(t.id))
                          .flatMap((t) => t.sources)
                      )).slice(0, 5).map((src) => (
                        <div key={src} className="flex items-center gap-2 text-xs text-slate-400">
                          <div className="w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />
                          {src}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default TrendDiscovery;
